import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, ClipboardCheck, Eye, ScanEye } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { FINDING_STATUS, INSPECTION_STATUS, SEVERITY, VISION_STATUS, fmtDate } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ToneChip } from "../../components/common";
import { ActionButtons, useProjectPicker } from "../../components/ops";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TABS = [
  { id: "inspecciones", label: "Inspecciones", icon: ClipboardCheck },
  { id: "hallazgos", label: "Hallazgos y NCR", icon: ScanEye },
  { id: "vision", label: "Visión IA (candidatos)", icon: Eye },
];

function InspeccionesTab({ pid, users }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const { data } = useQuery({ queryKey: ["ops-quality", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/ops/calidad`)).data });
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/calidad/inspecciones`, f)).data,
    onSuccess: () => { toast.success("Inspección programada"); setOpen(false); setF({}); qc.invalidateQueries({ queryKey: ["ops-quality", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const items = data?.inspections || [];
  return (
    <div className="space-y-3" data-testid="inspecciones-tab">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setOpen(true)} data-testid="create-inspection-open">Nueva inspección</Button>
      </div>
      {!items.length ? <EmptyState title="Sin inspecciones" description="Programa la primera inspección." /> : (
        <Table data-testid="inspecciones-table">
          <TableHeader><TableRow>
            <TableHead>Título</TableHead><TableHead>Estado</TableHead><TableHead>Inspector</TableHead>
            <TableHead>Fecha</TableHead><TableHead>Resultado</TableHead><TableHead>Validada por</TableHead><TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`inspection-row-${i.id}`}>
                <TableCell className="max-w-64"><p className="font-medium text-slate-800">{i.title}</p>
                  <p className="text-xs text-slate-400">{i.scope || ""}</p></TableCell>
                <TableCell><Chip map={INSPECTION_STATUS} value={i.status} testid={`inspection-status-${i.id}`} /></TableCell>
                <TableCell className="text-xs">{userName(users.map, i.inspector_id)}</TableCell>
                <TableCell className="text-xs">{fmtDate(i.planned_date)}</TableCell>
                <TableCell className="max-w-48 truncate text-xs text-slate-500">{i.result || "—"}</TableCell>
                <TableCell className="text-xs">{i.validated_by ? userName(users.map, i.validated_by) : "—"}</TableCell>
                <TableCell><ActionButtons item={i} endpoint={`/proyectos/${pid}/ops/calidad/inspecciones`} invalidate={[["ops-quality", pid]]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-inspection-dialog">
          <DialogHeader><DialogTitle>Nueva inspección</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="create-inspection-title" /></div>
            <div><Label>Alcance</Label><Input value={f.scope || ""} onChange={(e) => setF({ ...f, scope: e.target.value })} data-testid="create-inspection-scope" /></div>
            <div><Label>Fecha prevista</Label><Input type="date" value={f.planned_date?.slice(0, 10) || ""} onChange={(e) => setF({ ...f, planned_date: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="create-inspection-date" /></div>
            <Button data-testid="create-inspection-submit" disabled={!f.title || mut.isPending} onClick={() => mut.mutate()}>Programar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HallazgosTab({ pid, users }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const { data } = useQuery({ queryKey: ["ops-quality", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/ops/calidad`)).data });
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/calidad/hallazgos`, f)).data,
    onSuccess: () => { toast.success("Hallazgo registrado"); setOpen(false); setF({}); qc.invalidateQueries({ queryKey: ["ops-quality", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const items = data?.findings || [];
  return (
    <div className="space-y-3" data-testid="hallazgos-tab">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500">Flujo: inspección → hallazgo → validación → NCR → acción correctiva → evidencia → verificación → cierre. Una NCR nunca se cierra cambiando el estado a mano.</p>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="create-finding-open">Nuevo hallazgo</Button>
      </div>
      {!items.length ? <EmptyState title="Sin hallazgos" description="Los hallazgos nacen de inspecciones o de visión IA confirmada." /> : (
        <Table data-testid="hallazgos-table">
          <TableHeader><TableRow>
            <TableHead>Título</TableHead><TableHead>Estado</TableHead><TableHead>Severidad</TableHead><TableHead>Bloqueos</TableHead>
            <TableHead>Origen</TableHead><TableHead>Responsable</TableHead><TableHead>Evidencia</TableHead><TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((x) => (
              <TableRow key={x.id} data-testid={`finding-row-${x.id}`}>
                <TableCell className="max-w-64"><p className="font-medium text-slate-800">{x.title}</p>
                  <p className="text-xs text-slate-400">{x.location || x.id}</p></TableCell>
                <TableCell><Chip map={FINDING_STATUS} value={x.status} testid={`finding-status-${x.id}`} /></TableCell>
                <TableCell><Chip map={SEVERITY} value={x.severity} testid={`finding-severity-${x.id}`} /></TableCell>
                <TableCell className="space-x-1" data-testid={`finding-blocks-${x.id}`}>
                  {x.blocks_certification && <ToneChip label="Bloquea certificaciones" tone="danger" testid={`block-cert-${x.id}`} />}
                  {x.blocks_payment && <ToneChip label="Bloquea pagos" tone="danger" testid={`block-pay-${x.id}`} />}
                  {!x.blocks_certification && !x.blocks_payment && <span className="text-xs text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-xs">{x.origin === "vision_ia_confirmado" ? "Visión IA (confirmada)" : x.origin}</TableCell>
                <TableCell className="text-xs">{userName(users.map, x.assignee_id || x.responsible_id)}</TableCell>
                <TableCell className="max-w-40 truncate text-xs text-slate-500" title={x.evidence || ""}>{x.evidence || "—"}</TableCell>
                <TableCell><ActionButtons item={x} endpoint={`/proyectos/${pid}/ops/calidad/hallazgos`} invalidate={[["ops-quality", pid]]}
                  extraFields={{ elevar_ncr: [], iniciar_accion: [] }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-finding-dialog">
          <DialogHeader><DialogTitle>Nuevo hallazgo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="create-finding-title" /></div>
            <div><Label>Descripción</Label><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="create-finding-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ubicación</Label><Input value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="create-finding-location" /></div>
              <div><Label>Severidad</Label>
                <Select value={f.severity || "menor"} onValueChange={(v) => setF({ ...f, severity: v })}>
                  <SelectTrigger data-testid="create-finding-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{["menor", "mayor", "critica"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.blocks_certification} onChange={(e) => setF({ ...f, blocks_certification: e.target.checked })} data-testid="create-finding-block-cert" /> Bloquea certificaciones</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.blocks_payment} onChange={(e) => setF({ ...f, blocks_payment: e.target.checked })} data-testid="create-finding-block-pay" /> Bloquea pagos</label>
            </div>
            <Button data-testid="create-finding-submit" disabled={!f.title || mut.isPending} onClick={() => mut.mutate()}>Registrar hallazgo</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VisionTab({ pid, users }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const { data } = useQuery({ queryKey: ["ops-quality", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/ops/calidad`)).data });
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/vision`, f)).data,
    onSuccess: () => { toast.success("Detección IA simulada: candidato creado (pendiente de revisión humana)"); setOpen(false); setF({}); qc.invalidateQueries({ queryKey: ["ops-quality", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const items = data?.vision || [];
  return (
    <div className="space-y-3" data-testid="vision-tab">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500">La IA de visión <strong>solo crea hallazgos candidatos</strong>: nunca una no conformidad definitiva. Flujo: detección → revisión humana → descartar/confirmar → incidencia/NCR → acción → evidencia → verificación.</p>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="vision-simulate-open">
          <Camera className="mr-1 h-3.5 w-3.5" /> Simular detección IA
        </Button>
      </div>
      {!items.length ? <EmptyState title="Sin candidatos de visión" description="Simula una detección IA para ver el flujo de revisión humana." /> : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="vision-grid">
          {items.map((v) => (
            <div key={v.id} className="ts-card p-4" data-testid={`vision-card-${v.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-slate-800">{v.title}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{v.location} · confianza IA {(v.confidence * 100).toFixed(0)}% · {fmtDate(v.created_at)}</p>
                </div>
                <Chip map={VISION_STATUS} value={v.status} testid={`vision-status-${v.id}`} />
              </div>
              <p className="mt-2 text-xs text-violet-600" data-testid={`vision-disclaimer-${v.id}`}>
                {v.status === "pendiente_revision" ? "Detección IA demo — requiere revisión humana; no es una no conformidad"
                  : v.status === "confirmado" ? "Detección IA demo — revisada y confirmada por un humano"
                  : "Detección IA demo — revisada y descartada por un humano"}
              </p>
              {v.finding_id && <p className="mt-1 text-xs text-slate-500">→ derivó en registro <span className="font-mono">{v.finding_id}</span></p>}
              {v.discard_reason && <p className="mt-1 text-xs text-slate-500">Motivo de descarte: {v.discard_reason}</p>}
              <div className="mt-3">
                <ActionButtons item={v} endpoint={`/proyectos/${pid}/ops/vision`} invalidate={[["ops-quality", pid]]}
                  extraFields={{ confirmar: ["severity", "responsible_id"] }} />
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="vision-simulate-dialog">
          <DialogHeader><DialogTitle>Simular detección de visión IA (demo)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Descripción de la detección *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="vision-new-title" /></div>
            <div><Label>Ubicación</Label><Input value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="vision-new-location" /></div>
            <div><Label>Confianza (0-1)</Label><Input type="number" step="0.01" value={f.confidence ?? 0.72} onChange={(e) => setF({ ...f, confidence: Number(e.target.value) })} data-testid="vision-new-confidence" /></div>
            <Button data-testid="vision-simulate-submit" disabled={!f.title || mut.isPending} onClick={() => mut.mutate()}>Crear candidato</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Calidad() {
  const [sp, setSp] = useSearchParams();
  const [pid, picker] = useProjectPicker();
  const tab = sp.get("tab") || "inspecciones";
  const users = useUsers();
  return (
    <div className="space-y-4" data-testid="calidad-page">
      <PageHeader title="Calidad y no conformidades"
        subtitle="Inspección → hallazgo → validación → NCR → acción correctiva → evidencia → verificación → cierre. Las NCR pueden bloquear certificaciones y pagos reales."
        actions={picker} testid="calidad-title" />
      <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-px">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setSp((p) => { p.set("tab", t.id); return p; })}
            className={`flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors ${tab === t.id ? "bg-white text-blue-800 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-800"}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </button>
        ))}
      </div>
      {tab === "inspecciones" && <InspeccionesTab pid={pid} users={users} />}
      {tab === "hallazgos" && <HallazgosTab pid={pid} users={users} />}
      {tab === "vision" && <VisionTab pid={pid} users={users} />}
    </div>
  );
}
